import { forwardRef } from "react";
import { Building2, Users } from "lucide-react";
import { cn } from "@/lib/cn";
import { Badge } from "./Badge";
import { Avatar, getInitials } from "./Avatar";

/**
 * TenantBadge — workspace identifier (Faza 8).
 *
 * Use-case'lar:
 *   - Sidebar settings header (current workspace ko'rsatish)
 *   - TenantSwitcher list item
 *   - Members page header
 *
 * Variantlar:
 *   - row (default): horizontal — avatar + nom + plan + members
 *   - block: vertical — kattaroq, settings header'da
 *   - compact: faqat avatar + nom (collapsed sidebar)
 *
 * Props:
 *   - plan: free/pro/enterprise (Badge bilan)
 *   - memberCount: optional users.svg + count
 *   - logoUrl: optional avatar (yo'q bo'lsa initial)
 */
export type TenantPlan = "free" | "pro" | "enterprise";

export interface TenantBadgeProps {
  /** Workspace nomi */
  name: string;
  /** Plan turi */
  plan?: TenantPlan;
  /** A'zolar soni */
  memberCount?: number;
  /** Workspace logosi URL */
  logoUrl?: string | null;
  /** Slug yoki sub-text (agar memberCount yo'q bo'lsa) */
  subtitle?: string;
  variant?: "row" | "block" | "compact";
  /** Aktiv (current) workspace ekanligini ko'rsatish (gradient border) */
  active?: boolean;
  className?: string;
  onClick?: () => void;
}

const PLAN_BADGE: Record<TenantPlan, { variant: Parameters<typeof Badge>[0]["variant"]; label: string }> = {
  free: { variant: "plan-free", label: "Free" },
  pro: { variant: "plan-pro", label: "Pro" },
  enterprise: { variant: "plan-enterprise", label: "Enterprise" },
};

export const TenantBadge = forwardRef<HTMLDivElement, TenantBadgeProps>(
  (
    {
      name,
      plan,
      memberCount,
      logoUrl,
      subtitle,
      variant = "row",
      active = false,
      className,
      onClick,
    },
    ref,
  ) => {
    const planMeta = plan ? PLAN_BADGE[plan] : null;
    const initials = getInitials(name);

    const avatar = (
      <Avatar
        size={variant === "block" ? "lg" : variant === "compact" ? "sm" : "md"}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`${name} logo`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[var(--accent-violet-soft)] text-[var(--accent-violet)]">
            {initials ? (
              <span className="text-xs font-semibold">{initials}</span>
            ) : (
              <Building2 className="h-4 w-4" aria-hidden />
            )}
          </div>
        )}
      </Avatar>
    );

    if (variant === "compact") {
      return (
        <div
          ref={ref}
          className={cn("inline-flex items-center gap-2", className)}
          onClick={onClick}
          role={onClick ? "button" : undefined}
        >
          {avatar}
          <span className="truncate text-sm font-medium text-[var(--text-primary)]">
            {name}
          </span>
        </div>
      );
    }

    if (variant === "block") {
      return (
        <div
          ref={ref}
          className={cn(
            "flex flex-col items-start gap-3 rounded-xl border p-4 transition-colors",
            active
              ? "border-[rgba(139,92,246,0.55)] bg-[var(--accent-violet-soft)] shadow-[var(--glow-sm)]"
              : "border-[var(--border-subtle)] bg-[var(--bg-surface)]",
            onClick && "cursor-pointer hover:border-[var(--border-strong)]",
            className,
          )}
          onClick={onClick}
          role={onClick ? "button" : undefined}
        >
          <div className="flex items-center gap-3 w-full">
            {avatar}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate text-base font-semibold text-[var(--text-primary)]">
                  {name}
                </span>
                {planMeta && (
                  <Badge size="sm" variant={planMeta.variant}>
                    {planMeta.label}
                  </Badge>
                )}
              </div>
              {subtitle && (
                <p className="text-xs text-[var(--text-secondary)] truncate">{subtitle}</p>
              )}
            </div>
          </div>
          {typeof memberCount === "number" && (
            <div className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
              <Users className="h-3.5 w-3.5" aria-hidden />
              <span className="tabular-nums">{memberCount}</span>
              <span>{memberCount === 1 ? "a'zo" : "a'zo"}</span>
            </div>
          )}
        </div>
      );
    }

    // row variant
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors",
          active && "bg-[var(--accent-violet-soft)]",
          onClick && "cursor-pointer hover:bg-[var(--bg-surface)]",
          className,
        )}
        onClick={onClick}
        role={onClick ? "button" : undefined}
      >
        {avatar}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-[var(--text-primary)]">
              {name}
            </span>
            {planMeta && (
              <Badge size="sm" variant={planMeta.variant}>
                {planMeta.label}
              </Badge>
            )}
          </div>
          {(subtitle || typeof memberCount === "number") && (
            <div className="flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
              {subtitle && <span className="truncate">{subtitle}</span>}
              {typeof memberCount === "number" && (
                <>
                  {subtitle && <span aria-hidden>·</span>}
                  <span className="inline-flex items-center gap-1">
                    <Users className="h-3 w-3" aria-hidden />
                    <span className="tabular-nums">{memberCount}</span>
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);
TenantBadge.displayName = "TenantBadge";
